/**
 * Merge Issues with Rewrites Utility
 *
 * Combines DramaIssue[] and SuggestedRewrite[] from the API response
 * into a unified InlineIssue[] structure for the UI.
 */

import type { DramaIssue, SuggestedRewrite } from '@dramadetector/shared';
import type { InlineIssue } from '../types';
import { ISSUE_TYPE_TITLES } from '../types';

export interface PrimaryRewriteCandidate {
  original: string;
  rewritten: string;
  reasoning: string;
  snippet: string;
  startIndex: number;
  endIndex: number;
  issueType: InlineIssue['type'];
  severity: InlineIssue['severity'];
}

export interface LegacyTransformationFallback {
  afterText?: string | null;
  insights?: string[] | null;
}

export interface PrimaryRewriteDisplay {
  rewritten: string;
  reasoning: string | null;
}

/** Format any issue type string (camelCase, kebab-case, Title Case, etc.) to Title Case display */
function formatIssueType(raw: string): string {
  return raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Generate explanation bullet points from issue and rewrite data
 */
function generateExplanation(
  _issue: DramaIssue,
  rewrite?: SuggestedRewrite
): string[] {
  const explanations: string[] = [];

  // Add reasoning from rewrite if available
  if (rewrite?.reasoning) {
    explanations.push(rewrite.reasoning);
  }

  // Add improvement areas from rewrite if available
  if (rewrite?.improvementAreas?.length) {
    explanations.push(...rewrite.improvementAreas.slice(0, 2));
  }

  // Limit to 3 items
  return explanations.slice(0, 3);
}

/**
 * Calculate drama reduction based on scoreWeight (preferred) or severity (fallback)
 *
 * When scoreWeight is available (from backend LLM response), uses it directly
 * as the percentage contribution. Otherwise falls back to severity-based heuristics.
 */
function calculateDramaReduction(
  severity: DramaIssue['severity'],
  scoreWeight?: number
): number {
  if (typeof scoreWeight === 'number' && scoreWeight > 0) {
    return Math.round(scoreWeight * 100);
  }
  switch (severity) {
    case 'critical':
      return 40;
    case 'high':
      return 35;
    case 'medium':
      return 25;
    case 'low':
      return 15;
    default:
      return 20;
  }
}

/**
 * Find matching rewrite for an issue based on snippet similarity
 */
function findMatchingRewrite(
  issue: DramaIssue,
  rewrites: SuggestedRewrite[]
): SuggestedRewrite | undefined {
  // Guard: Return undefined if issue.snippet is not a valid string
  if (!issue.snippet || typeof issue.snippet !== 'string') {
    return undefined;
  }

  const issueSnippetLower = issue.snippet.toLowerCase();

  // Try to find a rewrite that contains the issue snippet
  return rewrites.find((r) => {
    // Guard: Skip rewrites with invalid original strings
    if (!r.original || typeof r.original !== 'string') {
      return false;
    }
    const originalLower = r.original.toLowerCase();
    return (
      originalLower.includes(issueSnippetLower) ||
      issueSnippetLower.includes(originalLower)
    );
  });
}

/**
 * Find a fallback snippet from rewrites when issue.snippet is empty
 *
 * FIX (2026-01-23): Google Gemini doesn't reliably return snippet fields.
 * When snippet is empty, try to use rewrite.original as the snippet.
 * This allows inline annotations to display even with incomplete LLM responses.
 *
 * @param issue - The issue with potentially empty snippet
 * @param rewrites - Available rewrites with original text
 * @param message - Original message for validation
 * @param usedRanges - Already used ranges to avoid overlap
 * @returns Fallback snippet string or null if none found
 */
function findFallbackSnippet(
  issue: DramaIssue,
  rewrites: SuggestedRewrite[],
  message: string,
  usedRanges: Array<{ start: number; end: number }>
): string | null {
  // Only use fallback if snippet is truly empty
  if (issue.snippet && issue.snippet.trim().length > 0) {
    return issue.snippet;
  }

  // Try to find a rewrite.original that exists in the message
  for (const rewrite of rewrites) {
    if (!rewrite.original || typeof rewrite.original !== 'string') {
      continue;
    }

    // Check if this original text exists in message and hasn't been used
    const foundIndex = message.indexOf(rewrite.original);
    if (foundIndex !== -1) {
      const endIndex = foundIndex + rewrite.original.length;
      const overlaps = usedRanges.some(
        (range) => foundIndex < range.end && endIndex > range.start
      );

      if (!overlaps) {
        if (__DEV__) {
          console.log('[mergeIssuesWithRewrites] Using rewrite.original as fallback snippet:', {
            issueType: issue.type,
            fallbackSnippet: rewrite.original.substring(0, 50) + '...',
          });
        }
        return rewrite.original;
      }
    }
  }

  return null;
}

/**
 * Recalculate issue indices by finding snippet in original message
 *
 * LLMs are unreliable at providing accurate character indices.
 * This function finds the actual position of each snippet in the message.
 *
 * @param message - Original message text
 * @param snippet - Problematic text snippet from API
 * @param usedRanges - Already used ranges to avoid overlap
 * @returns { startIndex, endIndex } or null if not found
 */
function recalculateIndices(
  message: string,
  snippet: string,
  usedRanges: Array<{ start: number; end: number }>
): { startIndex: number; endIndex: number } | null {
  if (!snippet || !message) return null;

  // Try exact match first
  let searchStart = 0;
  let foundIndex = message.indexOf(snippet, searchStart);

  // Skip positions that overlap with already-used ranges
  while (foundIndex !== -1) {
    const endIndex = foundIndex + snippet.length;
    const overlaps = usedRanges.some(
      (range) => foundIndex < range.end && endIndex > range.start
    );

    if (!overlaps) {
      return { startIndex: foundIndex, endIndex };
    }

    // Try next occurrence
    searchStart = foundIndex + 1;
    foundIndex = message.indexOf(snippet, searchStart);
  }

  // Try case-insensitive match
  // Guard: message and snippet are already validated at function entry
  const lowerMessage = message.toLowerCase();
  const lowerSnippet = snippet.toLowerCase();
  searchStart = 0;
  foundIndex = lowerMessage.indexOf(lowerSnippet, searchStart);

  while (foundIndex !== -1) {
    const endIndex = foundIndex + snippet.length;
    const overlaps = usedRanges.some(
      (range) => foundIndex < range.end && endIndex > range.start
    );

    if (!overlaps) {
      return { startIndex: foundIndex, endIndex };
    }

    searchStart = foundIndex + 1;
    foundIndex = lowerMessage.indexOf(lowerSnippet, searchStart);
  }

  return null;
}

/**
 * Merge API issues and rewrites into UI-friendly InlineIssue array
 *
 * IMPORTANT: This function recalculates startIndex/endIndex from snippets
 * because LLMs are unreliable at providing accurate character indices.
 *
 * @param message - Original message text (for index recalculation)
 * @param issues - DramaIssue array from API
 * @param rewrites - SuggestedRewrite array from API
 * @returns InlineIssue array sorted by startIndex
 */
export function mergeIssuesWithRewrites(
  message: string,
  issues: DramaIssue[],
  rewrites: SuggestedRewrite[]
): InlineIssue[] {
  // Guard: Normalize rewrites to empty array if not valid (DRAMA-WEB-2A)
  const safeRewrites = Array.isArray(rewrites) ? rewrites : [];
  if (__DEV__ && safeRewrites !== rewrites) {
    console.warn('[mergeIssuesWithRewrites] rewrites is not an array, using empty array:', typeof rewrites);
  }

  const usedRanges: Array<{ start: number; end: number }> = [];
  const validIssues: InlineIssue[] = [];

  issues.forEach((issue, index) => {
    const matchingRewrite = findMatchingRewrite(issue, safeRewrites);

    // FIX (2026-01-23): Try fallback snippet when issue.snippet is empty
    // Google Gemini doesn't reliably return snippet fields, but rewrites have original text
    const effectiveSnippet = findFallbackSnippet(issue, safeRewrites, message, usedRanges);

    if (!effectiveSnippet) {
      // No valid snippet found (neither from issue nor from rewrites) - skip
      if (__DEV__) {
        console.warn('No valid snippet found for issue, skipping:', {
          issueSnippet: issue.snippet?.substring(0, 50),
          type: issue.type,
          hasRewrites: safeRewrites.length > 0,
        });
      }
      return;
    }

    // Recalculate indices from effective snippet - don't trust LLM indices
    const recalculated = recalculateIndices(message, effectiveSnippet, usedRanges);

    if (!recalculated) {
      // Snippet not found in message - skip this issue
      if (__DEV__) {
        console.warn('Snippet not found in message, skipping issue:', {
          snippet: effectiveSnippet?.substring(0, 50),
          type: issue.type,
        });
      }
      return;
    }

    // Mark this range as used
    usedRanges.push({ start: recalculated.startIndex, end: recalculated.endIndex });

    validIssues.push({
      id: index,
      type: issue.type,
      severity: issue.severity,
      title: issue.raw_type ? formatIssueType(issue.raw_type) : (ISSUE_TYPE_TITLES[issue.type] || formatIssueType(issue.type)),
      snippet: effectiveSnippet, // Use effective snippet (may be from rewrite.original)
      original: effectiveSnippet, // Use effective snippet for original text
      startIndex: recalculated.startIndex,
      endIndex: recalculated.endIndex,
      description: issue.description,
      suggestion: issue.description,
      replacement: matchingRewrite?.rewritten || '',
      status: 'pending',
      dramaReduction: calculateDramaReduction(issue.severity, issue.scoreWeight),
      scoreWeight: issue.scoreWeight,
      explanation: generateExplanation(issue, matchingRewrite),
    });
  });

  // Sort by startIndex for proper display order
  return validIssues.sort((a, b) => a.startIndex - b.startIndex);
}

export function selectPrimaryRewriteCandidate(
  message: string,
  issues: DramaIssue[],
  rewrites: SuggestedRewrite[]
): PrimaryRewriteCandidate | null {
  const mergedIssues = mergeIssuesWithRewrites(message, issues, rewrites);
  const firstMatchedIssue = mergedIssues.find((issue) => issue.replacement.trim().length > 0);

  if (!firstMatchedIssue) {
    return null;
  }

  return {
    original: firstMatchedIssue.original,
    rewritten: firstMatchedIssue.replacement,
    reasoning: firstMatchedIssue.explanation[0] ?? '',
    snippet: firstMatchedIssue.snippet,
    startIndex: firstMatchedIssue.startIndex,
    endIndex: firstMatchedIssue.endIndex,
    issueType: firstMatchedIssue.type,
    severity: firstMatchedIssue.severity,
  };
}

export function selectPrimaryRewriteDisplay(
  message: string,
  issues: DramaIssue[],
  rewrites: SuggestedRewrite[],
  transformations: LegacyTransformationFallback[] = []
): PrimaryRewriteDisplay | null {
  const candidate = selectPrimaryRewriteCandidate(message, issues, rewrites);

  if (candidate) {
    return {
      rewritten: candidate.rewritten,
      reasoning: candidate.reasoning,
    };
  }

  if (issues.length > 0) {
    return null;
  }

  const fallback = transformations[0];
  if (!fallback?.afterText) {
    return null;
  }

  return {
    rewritten: fallback.afterText,
    reasoning: fallback.insights?.[0] ?? null,
  };
}
